import React, { useMemo, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
} from '@material-ui/core'
import { DateTimePicker } from '@material-ui/pickers'
import CalendarTodayIcon from '@material-ui/icons/CalendarTodayOutlined'

import moment from 'config/moment'
import { ERRORS, STRINGS } from 'consts'
import { snackbar } from 'components'
import { useApi, useApiCall } from 'hooks'
import CustomTextField from 'components/CustomTextField'

import {
  createVisitPromotion,
  deleteVisitPromotion,
  getVisitPromotions,
  updateVisitPromotion,
} from './api'
import {
  CreateVisitPromotionPayload,
  CreateVisitPromotionResponse,
  GetVisitPromotionsResponse,
  UpdateVisitPromotionPayload,
  UpdateVisitPromotionResponse,
  VisitPromotion,
} from './types'
import { DefaultTable } from '../../components'

interface PromotionFormValues {
  title: string
  startTime: string
  endTime: string
  visitsToAdd: number
  enabled: boolean
}

const toInputDateTime = (isoDate: string): string => moment(isoDate).format('YYYY-MM-DDTHH:mm')

const columns = [
  { title: 'ID', field: 'id' },
  { title: 'Título', field: 'title' },
  {
    title: 'Inicio',
    render: (rowData: VisitPromotion) => moment(rowData.startTime).format('DD/MM/YYYY HH:mm'),
  },
  {
    title: 'Fin',
    render: (rowData: VisitPromotion) => moment(rowData.endTime).format('DD/MM/YYYY HH:mm'),
  },
  { title: 'Visitas a sumar', field: 'visitsToAdd' },
  {
    title: 'Estado',
    render: (rowData: VisitPromotion) => (rowData.enabled ? 'Activo' : 'Inactivo'),
    sorting: false,
  },
]

const getVisitPromotionsResponseGetter = (responseData: GetVisitPromotionsResponse) =>
  responseData?.promotions ?? []

const emptyForm: PromotionFormValues = {
  title: '',
  startTime: '',
  endTime: '',
  visitsToAdd: 2,
  enabled: true,
}

const VisitPromotions: React.FC = () => {
  const [{ data: promotions, isLoading }, , setPromotions] = useApi<
    GetVisitPromotionsResponse,
    VisitPromotion[]
  >(getVisitPromotions, getVisitPromotionsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [createPromotionApi, isCreating] = useApiCall<
    CreateVisitPromotionPayload,
    CreateVisitPromotionResponse
  >(createVisitPromotion)
  const [updatePromotionApi, isUpdating] = useApiCall<
    UpdateVisitPromotionPayload,
    UpdateVisitPromotionResponse
  >(updateVisitPromotion)
  const [deletePromotionApi, isDeleting] = useApiCall<number, { deletedId: number }>(
    deleteVisitPromotion,
  )

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [formValues, setFormValues] = useState<PromotionFormValues>(emptyForm)
  const [promotionToEdit, setPromotionToEdit] = useState<VisitPromotion | null>(null)

  const isSaving = isCreating || isUpdating

  const isInvalidRange = useMemo(() => {
    if (!formValues.startTime || !formValues.endTime) return false
    return moment(formValues.startTime).isSameOrAfter(moment(formValues.endTime))
  }, [formValues.endTime, formValues.startTime])

  const isSubmitDisabled =
    isSaving ||
    !formValues.title.trim() ||
    !formValues.startTime ||
    !formValues.endTime ||
    formValues.visitsToAdd < 1 ||
    isInvalidRange

  const closeDialog = () => {
    setIsDialogOpen(false)
    setPromotionToEdit(null)
    setFormValues(emptyForm)
  }

  const handleCreate = () => {
    setPromotionToEdit(null)
    setFormValues(emptyForm)
    setIsDialogOpen(true)
  }

  const handleEdit = (promotion: VisitPromotion) => {
    setPromotionToEdit(promotion)
    setFormValues({
      title: promotion.title,
      startTime: toInputDateTime(promotion.startTime),
      endTime: toInputDateTime(promotion.endTime),
      visitsToAdd: promotion.visitsToAdd,
      enabled: promotion.enabled,
    })
    setIsDialogOpen(true)
  }

  const handleDelete = async (promotion: VisitPromotion) => {
    try {
      await deletePromotionApi(promotion.id)
      setPromotions(promotions.filter(item => item.id !== promotion.id))
      snackbar.show('Día especial eliminado')
    } catch (error) {
      snackbar.show((error as Error)?.message || ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const handleSave = async () => {
    try {
      if (promotionToEdit) {
        const { promotion } = await updatePromotionApi({
          id: promotionToEdit.id,
          title: formValues.title.trim(),
          startTime: moment(formValues.startTime).toISOString(),
          endTime: moment(formValues.endTime).toISOString(),
          visitsToAdd: formValues.visitsToAdd,
          enabled: formValues.enabled,
        })
        setPromotions(promotions.map(item => (item.id === promotion.id ? promotion : item)))
        snackbar.show('Día especial actualizado')
      } else {
        const { promotion } = await createPromotionApi({
          title: formValues.title.trim(),
          startTime: moment(formValues.startTime).toISOString(),
          endTime: moment(formValues.endTime).toISOString(),
          visitsToAdd: formValues.visitsToAdd,
          enabled: formValues.enabled,
        })
        setPromotions([promotion, ...promotions])
        snackbar.show('Día especial creado')
      }
      closeDialog()
    } catch (error) {
      snackbar.show((error as Error)?.message || ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Crear nuevo día especial',
      isFreeAction: true,
      onClick: handleCreate,
    },
    () => ({
      icon: 'edit',
      tooltip: 'Editar',
      onClick: (_: React.SyntheticEvent, rowData: VisitPromotion | VisitPromotion[]) =>
        handleEdit(rowData as VisitPromotion),
    }),
    () => ({
      icon: 'delete',
      tooltip: 'Eliminar',
      disabled: isDeleting,
      onClick: (_: React.SyntheticEvent, rowData: VisitPromotion | VisitPromotion[]) =>
        handleDelete(rowData as VisitPromotion),
    }),
  ]

  return (
    <>
      <DefaultTable
        title="Días especiales de visitas"
        columns={columns}
        data={promotions}
        isLoading={isLoading}
        actions={actions}
      />
      <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{promotionToEdit ? 'Editar día especial' : 'Nuevo día especial'}</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            label="Título"
            fullWidth
            maxLength={120}
            value={formValues.title}
            onChange={event => setFormValues({ ...formValues, title: event.target.value })}
          />
          <DateTimePicker
            value={formValues.startTime ? moment(formValues.startTime) : null}
            onChange={date =>
              setFormValues({
                ...formValues,
                startTime: date ? date.toISOString() : '',
              })
            }
            autoOk
            disablePast
            format={STRINGS.DATE_TIME_FORMAT}
            initialFocusedDate={new Date()}
            label="Inicio"
            style={{ width: '100%' }}
            TextFieldComponent={props => (
              <CustomTextField
                {...props}
                margin="dense"
                fullWidth
                InputProps={{
                  ...props.InputProps,
                  endAdornment: (
                    <IconButton>
                      <CalendarTodayIcon style={{ width: 15, height: 15, color: '#6B7280' }} />
                    </IconButton>
                  ),
                }}
              />
            )}
          />
          <DateTimePicker
            value={formValues.endTime ? moment(formValues.endTime) : null}
            onChange={date =>
              setFormValues({
                ...formValues,
                endTime: date ? date.toISOString() : '',
              })
            }
            autoOk
            disablePast
            format={STRINGS.DATE_TIME_FORMAT}
            minDate={formValues.startTime ? moment(formValues.startTime) : moment()}
            minDateMessage="La fecha de fin debe ser mayor o igual a la fecha de inicio"
            label="Fin"
            style={{ width: '100%' }}
            TextFieldComponent={props => (
              <CustomTextField
                {...props}
                margin="dense"
                fullWidth
                error={isInvalidRange}
                helperText={isInvalidRange ? 'La fecha de inicio debe ser menor que la de fin' : ''}
                InputProps={{
                  ...props.InputProps,
                  endAdornment: (
                    <IconButton>
                      <CalendarTodayIcon style={{ width: 15, height: 15, color: '#6B7280' }} />
                    </IconButton>
                  ),
                }}
              />
            )}
          />
          <TextField
            margin="dense"
            label="Visitas a sumar"
            type="number"
            fullWidth
            value={formValues.visitsToAdd}
            onChange={event =>
              setFormValues({
                ...formValues,
                visitsToAdd: Number(event.target.value),
              })
            }
            inputProps={{ min: 1, max: 50 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formValues.enabled}
                onChange={event => setFormValues({ ...formValues, enabled: event.target.checked })}
                color="primary"
              />
            }
            label="Día especial habilitado"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="default">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary" disabled={isSubmitDisabled}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export { VisitPromotions }
